import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const TECHNIQUES = [
  {
    key: 'box',
    label: 'Box Breathing',
    description: 'Used by athletes & military to stay calm under pressure',
    color: '#4ade80',
    phases: [
      { name: 'Inhale',  duration: 4, instruction: 'Breathe in slowly...'    },
      { name: 'Hold',    duration: 4, instruction: 'Hold your breath...'      },
      { name: 'Exhale',  duration: 4, instruction: 'Breathe out slowly...'    },
      { name: 'Hold',    duration: 4, instruction: 'Hold your breath...'      },
    ],
  },
  {
    key: '478',
    label: '4-7-8 Breathing',
    description: 'Powerful technique to reduce anxiety instantly',
    color: '#22d3ee',
    phases: [
      { name: 'Inhale',  duration: 4, instruction: 'Breathe in through nose...' },
      { name: 'Hold',    duration: 7, instruction: 'Hold...'                     },
      { name: 'Exhale',  duration: 8, instruction: 'Breathe out through mouth...' },
    ],
  },
  {
    key: 'calm',
    label: 'Calm Breath',
    description: 'Simple and gentle — great for beginners',
    color: '#a78bfa',
    phases: [
      { name: 'Inhale',  duration: 5, instruction: 'Breathe in gently...'  },
      { name: 'Exhale',  duration: 5, instruction: 'Breathe out gently...' },
    ],
  },
];

const AFFIRMATIONS = [
  'You are stronger than you know.',
  'This moment will pass.',
  'You are enough, exactly as you are.',
  'Your feelings are valid.',
  'You are not alone in this.',
  'One breath at a time.',
  'You have survived every hard day so far.',
  'Peace is available to you right now.',
];

export default function Breathing() {
  const navigate = useNavigate();

  const [selected,    setSelected]    = useState(TECHNIQUES[0]);
  const [running,     setRunning]     = useState(false);
  const [phaseIdx,    setPhaseIdx]    = useState(0);
  const [countdown,   setCountdown]   = useState(0);
  const [cycles,      setCycles]      = useState(0);
  const [scale,       setScale]       = useState(1);
  const [affirmation, setAffirmation] = useState(AFFIRMATIONS[0]);
 

  const timerRef  = useRef(null);
  const rippleRef = useRef(null);

  const currentPhase = selected.phases[phaseIdx];
  const accentColor  = selected.color;

  // Pick a random affirmation on mount
  useEffect(() => {
    const idx = Math.floor(Math.random() * AFFIRMATIONS.length);
    setAffirmation(AFFIRMATIONS[idx]);
  }, []);

  // Ripple animation
  useEffect(() => {
    if (running) {
      rippleRef.current = setInterval(() => {
        
      }, 2000);
    } else {
      clearInterval(rippleRef.current);
      
    }
    return () => clearInterval(rippleRef.current);
  }, [running]);

  // Main breathing timer
  useEffect(() => {
    clearInterval(timerRef.current);
    if (!running) return;

    setCountdown(currentPhase.duration);

    // Animate circle
    if (currentPhase.name === 'Inhale') {
      setScale(1.45);
    } else if (currentPhase.name === 'Exhale') {
      setScale(1);
    }
    // Hold keeps current scale

    timerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          // Move to next phase
          setPhaseIdx(pi => {
            const next = (pi + 1) % selected.phases.length;
            if (next === 0) setCycles(c => c + 1);
            return next;
          });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [running, phaseIdx, selected]); // eslint-disable-line react-hooks/exhaustive-deps

  function startStop() {
    if (running) {
      setRunning(false);
      setPhaseIdx(0);
      setCountdown(0);
      setScale(1);
      setCycles(0);
    } else {
      setPhaseIdx(0);
      setCycles(0);
      setRunning(true);
    }
  }

  function selectTechnique(t) {
    if (running) {
      setRunning(false);
      setPhaseIdx(0);
      setCountdown(0);
      setScale(1);
      setCycles(0);
    }
    setSelected(t);
  }

  const phaseColor = currentPhase?.name === 'Inhale'  ? accentColor
                   : currentPhase?.name === 'Exhale'  ? '#7fa8c8'
                   : '#fbbf24';

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a1628 0%, #0d1f3c 50%, #0a2040 100%)',
      px: { xs: 2, sm: 4 },
      py: 4,
      color: '#e8f4f8',
      display: 'flex',
      flexDirection: 'column',
    }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Button
          onClick={() => navigate('/dashboard')}
          startIcon={<ArrowBack />}
          sx={{ color: '#7fa8c8', textTransform: 'none', minWidth: 0, p: 0 }}
        />
        <Box>
          <Typography sx={{
            fontSize: { xs: '1.8rem', sm: '2.2rem' },
            fontWeight: 800,
            background: 'linear-gradient(90deg, #4ade80, #22d3ee)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.5px',
          }}>
            Breathe 🌬️
          </Typography>
          <Typography sx={{ color: '#7fa8c8', fontSize: '0.9rem', mt: 0.5 }}>
            Take a moment for yourself
          </Typography>
        </Box>
      </Box>

      {/* Technique Selector */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 4 }}>
        {TECHNIQUES.map(t => (
          <Chip
            key={t.key}
            label={t.label}
            onClick={() => selectTechnique(t)}
            sx={{
              background: selected.key === t.key ? `${t.color}22` : 'rgba(255,255,255,0.05)',
              color: selected.key === t.key ? t.color : '#7fa8c8',
              border: selected.key === t.key
                ? `1px solid ${t.color}55`
                : '1px solid rgba(255,255,255,0.08)',
              fontWeight: selected.key === t.key ? 700 : 400,
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': { background: `${t.color}15`, color: t.color },
            }}
          />
        ))}
      </Box>

      {/* Technique description */}
      <Typography sx={{
        color: '#7fa8c8', fontSize: '0.85rem', mb: 4,
        textAlign: 'center', fontStyle: 'italic',
      }}>
        {selected.description}
      </Typography>

      {/* ── Breathing Animation ── */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        py: 2,
      }}>

        {/* Ripple rings + main circle */}
        <Box sx={{ position: 'relative', width: 260, height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>

          {/* Ripple ring 1 */}
          <Box sx={{
            position: 'absolute',
            width: 260, height: 260,
            borderRadius: '50%',
            border: `1px solid ${accentColor}`,
            opacity: running ? 0.15 : 0,
            transform: running ? 'scale(1.3)' : 'scale(1)',
            transition: 'all 2s ease-in-out',
            animation: running ? 'ripple1 3s ease-out infinite' : 'none',
            '@keyframes ripple1': {
              '0%':   { transform: 'scale(1)',   opacity: 0.2 },
              '100%': { transform: 'scale(1.6)', opacity: 0   },
            },
          }} />

          {/* Ripple ring 2 */}
          <Box sx={{
            position: 'absolute',
            width: 260, height: 260,
            borderRadius: '50%',
            border: `1px solid ${accentColor}`,
            opacity: running ? 0.1 : 0,
            animation: running ? 'ripple2 3s ease-out 1s infinite' : 'none',
            '@keyframes ripple2': {
              '0%':   { transform: 'scale(1)',   opacity: 0.15 },
              '100%': { transform: 'scale(1.8)', opacity: 0    },
            },
          }} />

          {/* Outer glow ring */}
          <Box sx={{
            position: 'absolute',
            width: 220, height: 220,
            borderRadius: '50%',
            border: `2px solid ${accentColor}33`,
            transform: `scale(${scale * 0.95})`,
            transition: `transform ${currentPhase?.name === 'Inhale' ? currentPhase.duration : currentPhase?.name === 'Exhale' ? currentPhase.duration : 0.3}s ease-in-out`,
          }} />

          {/* Main breathing circle */}
          <Box sx={{
            width: 180, height: 180,
            borderRadius: '50%',
            background: `radial-gradient(circle at 35% 35%, ${accentColor}40, ${accentColor}15)`,
            border: `2px solid ${accentColor}60`,
            transform: `scale(${scale})`,
            transition: `transform ${currentPhase?.name === 'Inhale' ? currentPhase.duration : currentPhase?.name === 'Exhale' ? currentPhase.duration : 0.3}s ease-in-out`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: running ? `0 0 40px ${accentColor}30, 0 0 80px ${accentColor}15` : 'none',
            position: 'relative',
            zIndex: 2,
          }}>

            {/* Floating particles inside circle */}
            {running && [0,1,2].map(i => (
              <Box key={i} sx={{
                position: 'absolute',
                width: 6, height: 6,
                borderRadius: '50%',
                background: accentColor,
                opacity: 0.4,
                top: `${20 + i * 25}%`,
                left: `${20 + i * 20}%`,
                animation: `float${i} ${2 + i}s ease-in-out infinite alternate`,
                [`@keyframes float${i}`]: {
                  from: { transform: 'translateY(0px)', opacity: 0.3 },
                  to:   { transform: 'translateY(-12px)', opacity: 0.7 },
                },
              }} />
            ))}

            {!running ? (
              <Typography sx={{ fontSize: '2.5rem' }}>🌬️</Typography>
            ) : (
              <>
                <Typography sx={{
                  fontSize: '2.8rem',
                  fontWeight: 800,
                  color: phaseColor,
                  lineHeight: 1,
                  transition: 'color 0.5s',
                }}>
                  {countdown}
                </Typography>
                <Typography sx={{
                  fontSize: '0.7rem',
                  color: phaseColor,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 2,
                  mt: 0.5,
                  transition: 'color 0.5s',
                }}>
                  {currentPhase?.name}
                </Typography>
              </>
            )}
          </Box>
        </Box>

        {/* Phase instruction */}
        <Box sx={{ textAlign: 'center', minHeight: 48 }}>
          {running ? (
            <Typography sx={{
              fontSize: '1.1rem',
              color: '#e8f4f8',
              fontWeight: 500,
              animation: 'fadeIn 0.5s ease',
              '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'translateY(8px)' },
                to:   { opacity: 1, transform: 'translateY(0)'    },
              },
            }}>
              {currentPhase?.instruction}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: '0.95rem', color: '#7fa8c8' }}>
              Press start when you're ready
            </Typography>
          )}
        </Box>

        {/* Cycle counter */}
        {running && cycles > 0 && (
          <Box sx={{
            background: `${accentColor}15`,
            border: `1px solid ${accentColor}30`,
            borderRadius: 20,
            px: 2, py: 0.5,
          }}>
            <Typography sx={{ fontSize: '0.8rem', color: accentColor, fontWeight: 600 }}>
              {cycles} {cycles === 1 ? 'cycle' : 'cycles'} completed ✨
            </Typography>
          </Box>
        )}

        {/* Phase progress dots */}
        {running && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {selected.phases.map((p, i) => (
              <Box key={i} sx={{
                width: i === phaseIdx ? 24 : 8,
                height: 8,
                borderRadius: 4,
                background: i === phaseIdx ? accentColor : `${accentColor}30`,
                transition: 'all 0.3s ease',
              }} />
            ))}
          </Box>
        )}

        {/* Start / Stop button */}
        <Button
          onClick={startStop}
          sx={{
            background: running
              ? 'rgba(255,255,255,0.06)'
              : `linear-gradient(135deg, ${accentColor}, #22d3ee)`,
            color: running ? '#7fa8c8' : '#0a1628',
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: 3,
            px: 5, py: 1.5,
            fontSize: '1rem',
            border: running ? '1px solid rgba(255,255,255,0.1)' : 'none',
            transition: 'all 0.3s',
            '&:hover': { opacity: 0.85 },
          }}
        >
          {running ? 'Stop' : 'Start Breathing'}
        </Button>

        {/* Affirmation */}
        <Box sx={{
          maxWidth: 320,
          textAlign: 'center',
          p: 2.5,
          borderRadius: 3,
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.06)',
        }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#3a5a7a', mb: 0.5, textTransform: 'uppercase', letterSpacing: 1.5 }}>
            Reminder
          </Typography>
          <Typography sx={{ fontSize: '0.95rem', color: '#7fa8c8', fontStyle: 'italic', lineHeight: 1.6 }}>
            "{affirmation}"
          </Typography>
        </Box>

      </Box>
    </Box>
  );
}